import React from 'react'

import { StoryList } from '@/shared/api/models'
import { StoryCard } from '@/shared/components/StoryCard/StoryCard'
import { featuredStoriesSectionData } from '@/slices/HomePage/mocks/featuredStories/data'
import { FeaturedStory } from '@/slices/HomePage/mocks/featuredStories/type'
import { Chip, Container, styled, Typography } from '@mui/material'

function toStoryCard(story: FeaturedStory): StoryList {
  return {
    id: story.id,
    title: story.title,
    cover_image_url: story.imageUrl,
    print_dimensions: { page_width_mm: 210, page_height_mm: 210 },
  } as StoryList
}

export const FeaturedStories: React.FC = () => {
  const { headline, tagline, stories } = featuredStoriesSectionData

  return (
    <StyledFullWidthWrapper>
      <Container maxWidth="lg">
        <StyledTextContainer>
          <StyledHeadline variant="h2">
            {headline}
          </StyledHeadline>
          <StyledTagline variant="body1">
            {tagline}
          </StyledTagline>
        </StyledTextContainer>

        <StyledGrid>
          {stories.map((story) => (
            story.comingSoon ? (
              <StyledComingSoonCard key={story.id}>
                <StyledComingSoonBookArea>
                  <StyledBlurredContent>
                    <StoryCard story={toStoryCard(story)} />
                  </StyledBlurredContent>
                  <StyledComingSoonOverlay>
                    <Chip
                      label="Coming Soon"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    />
                  </StyledComingSoonOverlay>
                </StyledComingSoonBookArea>
              </StyledComingSoonCard>
            ) : (
              <StoryCard
                key={story.id}
                story={toStoryCard(story)}
                href={story.href}
              />
            )
          ))}
        </StyledGrid>
      </Container>
    </StyledFullWidthWrapper>
  )
}

const StyledFullWidthWrapper = styled('div')(({ theme }) => ({
  backgroundColor: '#FFF5EE',
  padding: theme.spacing(5, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledTextContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.headlineSectionGap.xs,

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.headlineSectionGap.sm,
  },
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: theme.palette.primary.main,
}))

const StyledTagline = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: theme.palette.primary.main,
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(3),

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
}))

const StyledComingSoonCard = styled('div')({
  pointerEvents: 'none',
  cursor: 'default',
})

const StyledComingSoonBookArea = styled('div')({
  position: 'relative',
})

const StyledBlurredContent = styled('div')({
  // Target StyledBookCover3DContainer: CardWrapper > BookWrapper > first-child
  '& > div > div > div:first-child': {
    filter: 'blur(4px)',
    opacity: 0.7,
  },
})

const StyledComingSoonOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})
